import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'

import ForcePasswordReset from './components/ForcePasswordReset'

import Shell from './components/Shell'
import { PortalLoadingScreen } from './components/PortalUX'
import { usePatient } from './hooks/usePatient'
import { isChunkLoadError, recoverFromChunkLoadError } from './lib/chunkRecovery'
import { isShopRoutePath, patientHidesShopTab } from './lib/portalAccess'
import MsoDashboard from './pages/MsoDashboard'

const Login = React.lazy(() => import('./pages/Login'))
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'))
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const GetStarted = React.lazy(() => import('./pages/GetStarted'))
const PatientLinkRouter = React.lazy(() => import('./pages/PatientLinkRouter'))
const AdminImpersonation = React.lazy(() => import('./pages/AdminImpersonation'))
const Visits = React.lazy(() => import('./pages/Visits'))
const Messages = React.lazy(() => import('./pages/Messages'))
const Prescriptions = React.lazy(() => import('./pages/Prescriptions'))
const Labs = React.lazy(() => import('./pages/Labs'))
const Forms = React.lazy(() => import('./pages/Forms'))

const MsoSignup = React.lazy(() => import('./pages/MsoSignup'))
const PurchaseLinkLanding = React.lazy(() => import('./pages/PurchaseLinkLanding'))
const MsoIntake = React.lazy(() => import('./pages/MsoIntake'))
const MsoOrders = React.lazy(() => import('./pages/MsoOrders'))
const MsoOrderDetail = React.lazy(() => import('./pages/MsoOrderDetail'))
const MsoRefillRequest = React.lazy(() => import('./pages/MsoRefillRequest'))
const MsoAccount = React.lazy(() => import('./pages/MsoAccount'))
const MsoHelpCenter = React.lazy(() => import('./pages/MsoHelpCenter'))
const MsoVisitJoin = React.lazy(() => import('./pages/MsoVisitJoin'))
const MsoGeneralIntake = React.lazy(() => import('./pages/MsoGeneralIntake'))
const MsoIdentityDetails = React.lazy(() => import('./pages/MsoIdentityDetails'))
const MsoSignatureForm = React.lazy(() => import('./pages/MsoSignatureForm'))
const Terms = React.lazy(() => import('./pages/policies/Terms'))
const Privacy = React.lazy(() => import('./pages/policies/Privacy'))
const NoticeOfPrivacyPractices = React.lazy(() => import('./pages/policies/NoticeOfPrivacyPractices'))
const TelehealthConsent = React.lazy(() => import('./pages/policies/TelehealthConsent'))
const PatientConsents = React.lazy(() => import('./pages/policies/PatientConsents'))

function PortalRouteFallback() {
  return <PortalLoadingScreen label="Loading your KIS Rx portal..." />
}

class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, recoveryFailed: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[PortalErrorBoundary] Caught:', error, info)
    const recoveryStarted = recoverFromChunkLoadError(error, { source: 'portal-error-boundary' })
    if (isChunkLoadError(error) && !recoveryStarted) {
      this.setState({ recoveryFailed: true })
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children
    const chunkLoadError = isChunkLoadError(this.state.error)
    if (chunkLoadError) {
      const waitingForUpdate = !this.state.recoveryFailed
      return (
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: '#10202B', background: '#F5F5F7' }}>
          <section role="status" aria-live="polite" style={{ width: 'min(560px, 100%)', padding: 28, boxSizing: 'border-box', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 18, background: '#fff', boxShadow: '0 18px 44px rgba(15,23,42,0.10)' }}>
            <div style={{ color: '#00A862', fontWeight: 900, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>KIS Rx update</div>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.2, color: '#10202B' }}>
              {waitingForUpdate ? 'Getting your latest KIS Rx' : 'Your KIS Rx update is ready'}
            </h1>
            <p style={{ margin: '12px 0 0', color: '#51606C', lineHeight: 1.6, fontWeight: 650 }}>
              {waitingForUpdate
                ? 'We are refreshing KIS Rx automatically. You will stay signed in and return to the same place.'
                : 'Tap below to open the newest version. Your account and health information are safe.'}
            </p>

            {waitingForUpdate ? (
              <div aria-hidden="true" style={{ width: 32, height: 32, marginTop: 22, border: '3px solid #CDEDDD', borderTopColor: '#00BF6D', borderRadius: '50%', animation: 'kisRxUpdateSpin 0.8s linear infinite' }} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{ width: '100%', minHeight: 48, marginTop: 22, padding: '12px 18px', borderRadius: 12, border: 0, background: '#00BF6D', color: '#fff', fontSize: 16, fontWeight: 900, cursor: 'pointer' }}
                >
                  Get the latest KIS Rx
                </button>
                <div style={{ marginTop: 24, paddingTop: 22, borderTop: '1px solid #E5ECE8' }}>
                  <h2 style={{ margin: 0, fontSize: 16, lineHeight: 1.35, color: '#10202B' }}>Still seeing this after the update?</h2>
                  <ol style={{ margin: '12px 0 0', paddingLeft: 22, color: '#51606C', lineHeight: 1.65, fontWeight: 650 }}>
                    <li>Close KIS Rx completely, then open it again.</li>
                    <li>If you use a Home Screen icon, touch and hold it and choose <strong>Remove App</strong> or <strong>Uninstall</strong>.</li>
                    <li>Open <strong>app.mykisrx.com</strong> in Safari or Chrome and sign in.</li>
                    <li>Choose <strong>Share → Add to Home Screen</strong> on iPhone/iPad, or <strong>Menu → Add to Home screen / Install app</strong> on Android.</li>
                  </ol>
                  <p style={{ margin: '12px 0 0', color: '#6B7A72', fontSize: 13, lineHeight: 1.5 }}>
                    Removing the Home Screen app does not delete your KIS Rx account or health information.
                  </p>
                </div>
              </>
            )}
            <style>{`@keyframes kisRxUpdateSpin { to { transform: rotate(360deg); } }`}</style>
          </section>
        </div>
      )
    }
    return (
      <div style={{ minHeight: '100vh', padding: 40, fontSize: 14, color: '#0F172A', background: '#F5F5F7' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Something went wrong</h2>
        <p style={{ maxWidth: 680, color: '#475569', lineHeight: 1.5 }}>
          The portal could not load this page. Please refresh and try again.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: 16, padding: '10px 16px', borderRadius: 10, border: 0, background: '#00BF6D', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
        >
          Reload Page
        </button>
      </div>
    )
  }
}

function ProtectedShell() {
  const { loading, session, patient, sourceUnavailable, error } = usePatient()
  const location = useLocation()
  const sessionUserId = session?.user?.id || ''
  const [passwordResetCompleteUserId, setPasswordResetCompleteUserId] = React.useState('')
  const passwordResetComplete = passwordResetCompleteUserId === sessionUserId

  if (loading) return <PortalLoadingScreen label="Preparing your portal..." />
  if (sourceUnavailable && session) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F7', display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box' }}>
        <div style={{ width: 'min(560px, 100%)', background: '#fff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 18, boxShadow: '0 18px 44px rgba(15,23,42,0.10)', padding: 28 }}>
          <div style={{ color: '#00A862', fontWeight: 900, fontSize: 12, letterSpacing: 0, textTransform: 'uppercase', marginBottom: 10 }}>KIS Rx</div>
          <h1 style={{ margin: '0 0 10px', fontSize: 24, lineHeight: 1.2, color: '#10202B' }}>Patient portal is temporarily unavailable.</h1>
          <p style={{ margin: 0, color: '#51606C', lineHeight: 1.55, fontWeight: 650 }}>
            {error || 'Supabase is not responding right now. Your session is still intact; refresh again shortly.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ marginTop: 20, height: 42, border: 0, borderRadius: 12, background: '#00BF6D', color: '#fff', fontWeight: 900, padding: '0 18px', cursor: 'pointer' }}
          >
            Refresh portal
          </button>
        </div>
      </div>
    )
  }
  if (!session || !patient) {
    const nextPath = `${location.pathname}${location.search}${location.hash}`
    const loginPath = nextPath && nextPath !== '/'
      ? `/login?next=${encodeURIComponent(nextPath)}`
      : '/login'
    return <Navigate to={loginPath} replace />
  }
  const mustResetPassword = !passwordResetComplete && (
    session.user?.user_metadata?.must_reset_password === true ||
    session.user?.user_metadata?.mustResetPassword === true
  )
  if (mustResetPassword) {
    return (
      <ForcePasswordReset
        email={session.user?.email}
        onComplete={() => setPasswordResetCompleteUserId(sessionUserId)}
      />
    )
  }

  const initialIntakeGateOpen =
    patient?.mso &&
    patient.onboarding?.initialIntakeRequired !== false &&
    patient.onboarding?.initialIntakeCompleted === false
  const profileSetupPaths = ['/profile-setup', '/forms/general-intake']
  const requestedDestination = `${location.pathname}${location.search}${location.hash}`
  const carriedNext = String(new URLSearchParams(location.search).get('next') || '').trim()
  const isPurchaseReturn = value => value.startsWith('/buy/') || /^\/dashboard\?purchaseSession=[0-9a-f-]+$/i.test(value)
  const onboardingNext = isPurchaseReturn(requestedDestination)
    ? requestedDestination
    : isPurchaseReturn(carriedNext) ? carriedNext : ''
  if (
    initialIntakeGateOpen &&
    !profileSetupPaths.includes(location.pathname)
  ) {
    return <Navigate to={`/profile-setup${onboardingNext ? `?next=${encodeURIComponent(onboardingNext)}` : ''}`} replace />
  }
  if (
    initialIntakeGateOpen &&
    profileSetupPaths.includes(location.pathname)
  ) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#F5F5F7',
        padding: '34px clamp(14px, 4vw, 44px) 46px',
        boxSizing: 'border-box',
      }}>
        <Outlet />
      </div>
    )
  }
  const identityDetailsPath = '/identity-details'
  const identityDetailsRouteOpen = location.pathname === identityDetailsPath
  const identityDetailsRedirectRequired =
    patient?.mso &&
    patient.onboarding?.initialIntakeGateSatisfied !== false &&
    patient.onboarding?.identityDetailsCompleted === false
  if (identityDetailsRedirectRequired && !identityDetailsRouteOpen) {
    return <Navigate to={`${identityDetailsPath}${onboardingNext ? `?next=${encodeURIComponent(onboardingNext)}` : ''}`} replace />
  }
  if (identityDetailsRedirectRequired && identityDetailsRouteOpen) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#F5F5F7',
        padding: '34px clamp(14px, 4vw, 44px) 46px',
        boxSizing: 'border-box',
      }}>
        <Outlet />
      </div>
    )
  }
  const consentPath = '/forms/patient-consents'
  const consentRouteOpen = location.pathname === consentPath
  const consentRedirectRequired =
    patient?.mso &&
    patient.onboarding?.initialIntakeGateSatisfied !== false &&
    patient.onboarding?.consentCompleted === false
  if (
    consentRedirectRequired &&
    !consentRouteOpen
  ) {
    return <Navigate to={`${consentPath}${onboardingNext ? `?next=${encodeURIComponent(onboardingNext)}` : ''}`} replace />
  }
  if (
    patient?.mso &&
    consentRouteOpen &&
    patient.onboarding?.consentCompleted !== true
  ) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#F5F5F7',
        padding: '34px clamp(14px, 4vw, 44px) 46px',
        boxSizing: 'border-box',
      }}>
        <Outlet />
      </div>
    )
  }
  if (patient?.mso && patientHidesShopTab(patient) && isShopRoutePath(location.pathname)) {
    return <Navigate to="/dashboard" replace />
  }
  return <Shell patient={patient} />
}

function DashboardWrapper() {
  const { patient } = usePatient()
  return <MsoDashboard patient={patient} />
}

function OrdersWrapper() {
  return <MsoOrders />
}

function MsoPathRedirect() {
  const location = useLocation()
  let cleanPath = location.pathname.replace(/^\/mso(?=\/|$)/, '') || '/'
  if (cleanPath === '/intake') cleanPath = '/shop'
  const searchParams = new URLSearchParams(location.search)
  if (cleanPath === '/shop' && searchParams.get('product') && !searchParams.has('open')) {
    searchParams.set('open', '1')
  }
  const search = searchParams.toString()
  return <Navigate to={`${cleanPath}${search ? `?${search}` : ''}${location.hash}`} replace />
}

function LoginRoute() {
  return <Login />
}

function PortalIndexRoute() {
  return <Navigate to="/dashboard" replace />
}

function FallbackRoute() {
  return <Navigate to="/dashboard" replace />
}

export default function PortalApp() {
  return (
    <PortalErrorBoundary>
      <BrowserRouter>
        <React.Suspense fallback={<PortalRouteFallback />}>
          <Routes>
          <Route index element={<PortalIndexRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/open" element={<PatientLinkRouter />} />
          <Route path="/link-router" element={<PatientLinkRouter />} />
          <Route path="/start/:token" element={<GetStarted />} />
          <Route path="/get-started/:token" element={<GetStarted />} />
          <Route path="/start" element={<GetStarted />} />
          <Route path="/impersonate" element={<AdminImpersonation />} />

          {/* MSO V1 public routes */}
          <Route path="/signup" element={<MsoSignup />} />
          <Route path="/buy/:routeKey" element={<PurchaseLinkLanding />} />
          <Route path="/newpatientintake" element={<MsoSignup directKisRxIntake />} />
          <Route path="/policies/terms" element={<Terms />} />
          <Route path="/policies/privacy" element={<Privacy />} />
          <Route path="/policies/npp" element={<NoticeOfPrivacyPractices />} />
          <Route path="/policies/telehealth-consent" element={<TelehealthConsent />} />
          <Route path="/policies/patient-consents" element={<PatientConsents />} />
          <Route path="/mso/*" element={<MsoPathRedirect />} />

          {/* MSO V1 authenticated routes (inside ProtectedShell) */}
          <Route element={<ProtectedShell />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardWrapper />} />
            <Route path="/visits" element={<Visits />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/prescriptions" element={<Prescriptions />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/orders" element={<OrdersWrapper />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/shop" element={<MsoIntake />} />
            <Route path="/intake" element={<MsoIntake />} />
            <Route path="/orders/:id" element={<MsoOrderDetail />} />
            <Route path="/refill" element={<MsoRefillRequest />} />
            <Route path="/refill/:productId" element={<MsoRefillRequest />} />
            <Route path="/help" element={<MsoHelpCenter />} />
            <Route path="/account" element={<MsoAccount />} />
            <Route path="/profile-setup" element={<MsoGeneralIntake />} />
            <Route path="/identity-details" element={<MsoIdentityDetails />} />
            <Route path="/forms/general-intake" element={<MsoGeneralIntake />} />
            <Route path="/forms/:formId" element={<MsoSignatureForm />} />
            <Route path="/verify-identity" element={<Navigate to="/forms" replace />} />
            <Route path="/visits/:id/join" element={<MsoVisitJoin />} />
          </Route>
          <Route path="*" element={<FallbackRoute />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </PortalErrorBoundary>
  )
}
